import { Helmet } from "react-helmet";
import { useState } from 'react';
import { useEffect } from 'react';
import { useSelector, shallowEqual } from "react-redux";
import { useHistory } from "react-router";
import { storefrontClient } from "../../App";
import { getCollectionByHandle } from "../../queries/storefront-graphql";
import { Loading } from "@merchstores/frontend/components/Loading";
import { getProductsByStoreTag } from "../../queries/storefront-graphql";
import { CollectionPageHeader } from "@merchstores/frontend/components/CollectionPage/CollectionPageHeader/component";
import { CollectionPageSortFilter } from "@merchstores/frontend/components/CollectionPage/CollectionPageSortFilter";
import { CollectionPageProductTable } from "@merchstores/frontend/components/CollectionPage/CollectionPageProductTable";
import { IMerchStore, IStoreState } from "@merchstores/frontend/reduxTypes";

export const CollectionPageLayout: React.FC = () => {
  const history = useHistory();
  const merchstore: IMerchStore = useSelector((state: IStoreState) => state.merchstore, shallowEqual)

  const [isLoading, setIsLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [sortChosen, setSortChosen] = useState('sort_default');

  const [productFilters, setProductFilters] = useState([]);
  const [brandFilters, setBrandFilters] = useState([]);

  const [productFilterChosen, setProductFilterChosen] = useState('All');
  const [priceFilterChosen, setPriceFilterChosen] = useState({ low: 0, high: 5000 });
  const [brandFilterChosen, setBrandFilterChosen] = useState('All');

  useEffect(() => {
    async function fetchData() {
      if (merchstore.baseCollectionHandle) {
        const products = await fetchCollectionProducts(merchstore.baseCollectionHandle, undefined);
        setAllProducts(products);
        setIsLoading(false);
      }
    }
    fetchData();
  }, [merchstore, sortChosen]);

  useEffect(() => {
    async function fetchFilteredData() {
      const storeCode = merchstore.storeCode
      if (productFilterChosen !== 'All' || brandFilterChosen !== 'All' || priceFilterChosen) {

        const filteredProducts = await fetchFilteredCollectionProducts(`merchstore_code=${storeCode}`, productFilterChosen, brandFilterChosen, priceFilterChosen, sortChosen, undefined);
        setFilteredProducts(filteredProducts);
        setIsLoading(false);
      } else {
        setFilteredProducts([])
      }
    }
    fetchFilteredData();
  }, [merchstore, sortChosen, productFilterChosen, brandFilterChosen, priceFilterChosen]);

  useEffect(() => {
    // Sets the available product & brand filters based on the store products
    if (allProducts) {
      const productTypeArray: string[] = ['All']
      const brandTypeArray: string[] = ['All']
      for (const product of allProducts) {
        if (!productTypeArray.includes(product.productType)) {
          productTypeArray.push(product.productType)
        }
      }
      for (const product of allProducts) {
        if (!brandTypeArray.includes(product.vendor)) {
          brandTypeArray.push(product.vendor)
        }
      }
      setProductFilters(productTypeArray.sort());
      setBrandFilters(brandTypeArray.sort());
    }

  }, [allProducts])

  const fetchCollectionProducts = async (baseCollectionHandle: string, cursor?: string) => {
    const { data, model } = await storefrontClient.send(getCollectionByHandle(baseCollectionHandle, cursor))
      .catch((err: any) => {
        console.error(err);
        throw err;
      });

    let result: any[] = [];
    if (model && model.collectionByHandle) {
      const collection = { ...model.collectionByHandle };
      result = result.concat(collection.products);
      setIsLoading(false)
    } else {
      console.log(`No collection of products found for store ${merchstore.baseCollectionHandle}`);
      history.push("/404");
    }

    const pageInfo = data.collectionByHandle.products.pageInfo;
    if (pageInfo.hasNextPage) {
      const nextCursor = data.collectionByHandle.products.edges[data.collectionByHandle.products.edges.length - 1].cursor;
      result = result.concat(await fetchCollectionProducts(baseCollectionHandle, nextCursor));
    }
    return result;
  }

  const fetchFilteredCollectionProducts = async (storeTag: string, productFilter: string, brandFilter: string, priceFilter: any, sortChosen: string, cursor?: string) => {

    const { data, model } = await storefrontClient.send(getProductsByStoreTag(storeTag, productFilter, brandFilter, priceFilter, sortChosen, cursor))

    let result: any[] = [];
    if (model && model.shop) {
      result = result.concat(model.shop.products);
    } else {
      console.log(`No collection of products found for store ${merchstore.baseCollectionHandle}`);
      history.push("/404");
    }

    const pageInfo = data.shop.products.pageInfo;
    if (pageInfo.hasNextPage) {
      const nextCursor = data.shop.products.edges[data.shop.products.edges.length - 1].cursor;
      result = result.concat(await fetchFilteredCollectionProducts(storeTag, productFilter, brandFilter, priceFilter, sortChosen, nextCursor));
    }
    return result;
  }

  if (isLoading) return <Loading isLoading={isLoading as boolean} />

  return (
    <>
      <Helmet>
        <meta
          name="description"
          content={merchstore.storeMessage}
        />
        <title>{merchstore.name}</title>
      </Helmet>

      <CollectionPageHeader merchstore={merchstore} />

      <CollectionPageSortFilter
        productFilters={productFilters}
        brandFilters={brandFilters}
        allProducts={allProducts}
        setSortChosen={setSortChosen}
        setProductFilterChosen={setProductFilterChosen}
        setPriceFilterChosen={setPriceFilterChosen}
        setBrandFilterChosen={setBrandFilterChosen} />

      <CollectionPageProductTable
        sortChosen={sortChosen}
        productFilterChosen={productFilterChosen}
        brandFilterChosen={brandFilterChosen}
        priceFilterChosen={priceFilterChosen}
        filteredProducts={filteredProducts}
        allProducts={allProducts} />
    </>
  )
}